import { useRef, type KeyboardEvent } from 'react';
import { CheckSquare, MessageSquare } from 'lucide-react';

import styles from './assistant.module.css';

export type AssistantTabId = 'chat' | 'pending';

const TABS: { id: AssistantTabId; label: string; Icon: typeof MessageSquare }[] = [
  { id: 'chat', label: 'Диалог', Icon: MessageSquare },
  { id: 'pending', label: 'Подтверждения', Icon: CheckSquare }
];

type AssistantTabsProps = {
  idPrefix: string;
  activeTab: AssistantTabId;
  onChange: (tab: AssistantTabId) => void;
  pendingCount: number;
  className?: string;
};

/**
 * Назначение: вкладки помощника с полной tab-семантикой и стрелочной навигацией.
 * Кто: страница «Помощник» и плавающее окно.
 * Вход: раздел «Помощник» или облако-кнопка в shell.
 */
export function AssistantTabs({ idPrefix, activeTab, onChange, pendingCount, className }: AssistantTabsProps) {
  const tabRefs = useRef<Partial<Record<AssistantTabId, HTMLButtonElement | null>>>({});

  function handleKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const currentIndex = TABS.findIndex((tab) => tab.id === activeTab);
    let nextIndex: number | null = null;

    switch (event.key) {
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % TABS.length;
        break;
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + TABS.length) % TABS.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = TABS.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    const nextTab = TABS[nextIndex];
    onChange(nextTab.id);
    tabRefs.current[nextTab.id]?.focus();
  }

  return (
    <div
      role="tablist"
      aria-label="Разделы помощника"
      className={`${styles.tabsScroller} ${styles.tabsRow} ${className ?? ''}`}
      onKeyDown={handleKeyDown}
    >
      {TABS.map((tab) => {
        const active = tab.id === activeTab;
        return (
          <button
            key={tab.id}
            ref={(node) => {
              tabRefs.current[tab.id] = node;
            }}
            type="button"
            role="tab"
            id={`${idPrefix}-tab-${tab.id}`}
            aria-controls={`${idPrefix}-tabpanel-${tab.id}`}
            aria-selected={active}
            tabIndex={active ? 0 : -1}
            className={`${styles.tabButton} ${active ? styles.tabActive : styles.tabIdle}`}
            onClick={() => onChange(tab.id)}
            data-testid={`${idPrefix}-tab-${tab.id}`}
          >
            <tab.Icon size={14} aria-hidden />
            {tab.label}
            {tab.id === 'pending' && pendingCount > 0 ? (
              <span className="ml-1 rounded-full bg-red-500 px-1.5 py-0.2 text-[10px] font-bold text-white">
                {pendingCount}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
